import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Check, Sparkles, BookOpen, Award, Headphones, 
  FileText, Zap, Clock, Loader2, ChevronRight, Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcademyMembership = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [activatedPlan, setActivatedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  // Check for success redirect
  React.useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      if (token) {
        verifyPayment(sessionId);
      } else {
        // User not logged in after Stripe redirect
        setPaymentSuccess(true);
        setActivatedPlan('your');
      }
    }
  }, [searchParams, token]);

  const verifyPayment = async (sessionId) => {
    setVerifying(true);
    try {
      const response = await axios.get(
        `${API}/academy/membership/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.status === 'completed' || response.data.status === 'active') {
        setPaymentSuccess(true);
        setActivatedPlan(response.data.plan || 'your');
        toast.success('Membership activated!');
      }
    } catch (error) {
      console.error('Verification error:', error);
      // Still show success since Stripe confirmed the payment
      setPaymentSuccess(true);
      setActivatedPlan('your');
    } finally {
      setVerifying(false);
    }
  };

  const handleCheckout = async (plan) => {
    if (!user) {
      toast.error('Please log in to purchase membership');
      navigate('/login?redirect=/academy/membership');
      return;
    }

    setLoading(plan);
    try {
      const response = await axios.post(
        `${API}/academy/membership/checkout`,
        { plan, origin_url: window.location.origin, coupon_code: appliedCoupon?.code || null },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(null);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    try {
      const { data } = await axios.post(`${API}/coupons/validate?code=${couponCode}`);
      if (data.valid) {
        setAppliedCoupon({ code: data.code, discount_percent: data.discount_percent });
        toast.success(`Coupon applied! ${data.discount_percent}% off`);
      } else {
        toast.error(data.message || 'Invalid coupon');
      }
    } catch {
      toast.error('Invalid coupon code');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const features = [
    { icon: BookOpen, text: "18 comprehensive courses" },
    { icon: FileText, text: "360+ in-depth lessons" },
    { icon: Headphones, text: "Audio versions of every lesson" },
    { icon: Zap, text: "Quizzes to test your knowledge" },
    { icon: Award, text: "Certificates of completion" },
    { icon: Clock, text: "Lifetime access to content" },
  ];

  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-slate-600">Verifying your payment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="membership-success-page">
        <Navbar />
        <div className="pt-24 flex items-center justify-center px-4">
          <Card className="max-w-md w-full border-green-200 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Payment Successful!
              </h2>
              <p className="text-slate-600 mb-6">
                Your {activatedPlan === 'yearly' ? 'Yearly' : activatedPlan === 'monthly' ? 'Monthly' : ''} AI Academy membership is now active. You have unlimited access to all 18 courses, 360+ lessons, quizzes, and certificates.
              </p>
              
              {user ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500">Logged in as <strong>{user.email}</strong></p>
                  <Button className="w-full" onClick={() => navigate('/academy/dashboard')} data-testid="go-to-dashboard-btn">
                    Go to Your Dashboard
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => navigate('/academy/courses')}>
                    Browse Courses
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> Please log in with the account you used to purchase to access your membership.
                    </p>
                  </div>
                  <Button className="w-full" onClick={() => navigate('/login?redirect=/academy/dashboard')} data-testid="login-after-payment-btn">
                    Log In to Access Courses
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="academy-membership-page">
      <Navbar />

      {/* Hero */}
      <div className="pt-16 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Badge className="bg-white/10 text-white border-white/20 mb-6">
            <Sparkles className="w-4 h-4 mr-2" />
            Unlimited Access
          </Badge>
          
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            AI Academy Membership
          </h1>
          
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Get unlimited access to all courses, lessons, quizzes, and certificates. Learn at your own pace.
          </p>
        </div>
      </div>

      {/* Coupon Code Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10 mb-6">
        <div className="bg-white rounded-xl border border-dashed border-indigo-300 p-4 flex flex-col sm:flex-row items-center justify-center gap-3" data-testid="coupon-section">
          <Tag className="w-5 h-5 text-indigo-500 flex-shrink-0" />
          <span className="text-sm text-slate-700 font-medium">Have a discount code?</span>
          {appliedCoupon ? (
            <div className="flex items-center gap-2" data-testid="coupon-applied">
              <Badge className="bg-green-100 text-green-700 border-green-200">
                <Check className="w-3 h-3 mr-1" /> {appliedCoupon.code} — {appliedCoupon.discount_percent}% off applied!
              </Badge>
              <button className="text-xs text-slate-400 hover:text-red-500 underline" onClick={() => setAppliedCoupon(null)} data-testid="remove-coupon-btn">Remove</button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Enter code"
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-44"
                value={couponCode}
                onChange={e => setCouponCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && applyCoupon()}
                data-testid="membership-coupon-input"
              />
              <Button size="sm" onClick={applyCoupon} disabled={applyingCoupon || !couponCode.trim()} data-testid="apply-membership-coupon-btn">
                {applyingCoupon ? 'Checking...' : 'Apply'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Monthly Plan */}
          <Card className="border-2 border-slate-200 hover:border-primary/50 transition-colors">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-xl">Monthly</CardTitle>
              <div className="mt-4">
                {appliedCoupon ? (
                  <>
                    <span className="text-2xl font-bold text-slate-400 line-through mr-2">$17</span>
                    <span className="text-4xl font-bold text-green-600">${(17 * (1 - appliedCoupon.discount_percent / 100)).toFixed(2)}</span>
                    <span className="text-slate-500">/month</span>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-slate-900">$17</span>
                    <span className="text-slate-500">/month</span>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-slate-600">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{feature.text}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => handleCheckout('monthly')}
                disabled={loading === 'monthly'}
                data-testid="monthly-plan-btn"
              >
                {loading === 'monthly' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Get Monthly Access'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Yearly Plan */}
          <Card className="border-2 border-primary shadow-xl shadow-primary/10 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-green-500 text-white border-0">
                Save $34/year
              </Badge>
            </div>
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-xl">Yearly</CardTitle>
              <div className="mt-4">
                {appliedCoupon ? (
                  <>
                    <span className="text-2xl font-bold text-slate-400 line-through mr-2">$170</span>
                    <span className="text-4xl font-bold text-green-600">${(170 * (1 - appliedCoupon.discount_percent / 100)).toFixed(2)}</span>
                    <span className="text-slate-500">/year</span>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-slate-900">$170</span>
                    <span className="text-slate-500">/year</span>
                  </>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {appliedCoupon 
                  ? `That's just $${((170 * (1 - appliedCoupon.discount_percent / 100)) / 12).toFixed(2)}/month`
                  : "That's just $14.17/month"
                }
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-slate-600">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{feature.text}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full"
                onClick={() => handleCheckout('yearly')}
                disabled={loading === 'yearly'}
                data-testid="yearly-plan-btn"
              >
                {loading === 'yearly' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Get Yearly Access
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-heading text-2xl font-bold text-center text-slate-900 mb-8">
          What's Included
        </h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: BookOpen,
              title: "18 Complete Courses",
              description: "ChatGPT, Claude, Prompt Engineering, AI Money, Content Creation, Social Media, Automation, and more"
            },
            {
              icon: FileText,
              title: "360+ Lessons",
              description: "Step-by-step tutorials with real-world examples and copy-paste prompts"
            },
            {
              icon: Headphones,
              title: "Audio Lessons",
              description: "Listen to lessons on the go with AI-generated audio versions"
            },
            {
              icon: Zap,
              title: "Interactive Quizzes",
              description: "Test your knowledge after each lesson with instant feedback"
            },
            {
              icon: Award,
              title: "Certificates",
              description: "Earn certificates for each completed course to showcase your skills"
            },
            {
              icon: Clock,
              title: "Learn at Your Pace",
              description: "No deadlines, no pressure. Access content anytime, anywhere"
            }
          ].map((item, idx) => (
            <Card key={idx} className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <item.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-sm text-slate-600">{item.description}</p>
            </Card>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-slate-200">
        <h2 className="font-heading text-2xl font-bold text-center text-slate-900 mb-8">
          Frequently Asked Questions
        </h2>
        
        <div className="space-y-6">
          {[
            {
              q: "Can I try before I buy?",
              a: "Yes! The first 2 lessons of every course are free to preview. No credit card required."
            },
            {
              q: "What if I'm not satisfied?",
              a: "We offer a 30-day money-back guarantee. If you're not happy, we'll refund your purchase."
            },
            {
              q: "How long do I have access?",
              a: "Your membership gives you access for the duration of your subscription (monthly or yearly)."
            },
            {
              q: "Are new courses included?",
              a: "Yes! Any new courses we add are automatically included in your membership."
            }
          ].map((item, idx) => (
            <div key={idx} className="bg-white rounded-lg p-6 border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-2">{item.q}</h3>
              <p className="text-slate-600">{item.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AcademyMembership;
